import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('pt');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('demarre-language');
    if (savedLanguage && ['pt', 'en', 'es'].includes(savedLanguage)) {
      setLanguage(savedLanguage);
      return;
    }
    // Auto-detect on FIRST visit (no saved preference).
    // Rule (Feb 2026): pt-* → PT; anything else → EN default. Covers
    // Brazilian visitors while keeping the site EN-first for other markets
    // (avoids the "US client sees PT copy" bug reported in production).
    try {
      const nav = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
      const detected = nav.startsWith('pt') ? 'pt' : 'en';
      setLanguage(detected);
      localStorage.setItem('demarre-language', detected);
    } catch {
      // Safe fallback: EN
      setLanguage('en');
      localStorage.setItem('demarre-language', 'en');
    }
  }, []);

  const changeLanguage = (lang) => {
    if (['pt', 'en', 'es'].includes(lang)) {
      setLanguage(lang);
      localStorage.setItem('demarre-language', lang);
      // Flag used by LanguagePreferenceModal to decide whether a logged-in
      // client's `preferred_language` should override the current UI
      // language on login. If the visitor explicitly picked a language in
      // this browser, their choice wins (we only auto-sync for first-time
      // visitors whose language was guessed from navigator.language).
      try { localStorage.setItem('demarre-language-explicit', '1'); } catch { /* ignore */ }
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};